import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  ActivityItem,
  ApiEnvelope,
  BoardExamResponse,
  ChildDashboardResponse,
  ChildReadinessResponse,
  ChildSubjectChapterRow,
  CramPlanRequest,
  CramPlanResponse,
  DoubtPattern,
  HeatmapDay,
  NotificationPrefs,
  ParentAlert,
  ParentChild,
  RedeemParentInviteRequest,
  RedeemParentInviteResponse,
  WeakTopic,
} from "@/lib/api-types";

/**
 * Parent-persona hooks -- every query/mutation mirrors a route in
 * backend/functions/staxed_parent_handler/index.mjs. The backend re-checks
 * role + link ownership on every route; these hooks don't duplicate that
 * logic, they just call it.
 */

/** GET /parent/children -> linked children (Phase 0 stub list; Phase 1 adds analytics). */
export function useParentChildren(enabled = true) {
  return useQuery({
    queryKey: qk.parent.children(),
    enabled,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ParentChild[]>>("/parent/children");
      return unwrap(res.data) ?? [];
    },
    staleTime: 30_000,
  });
}

/** POST /parent/links/redeem -> links the caller (a PARENT) to the inviting student. */
export function useRedeemParentInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RedeemParentInviteRequest) => {
      const res = await api.post<ApiEnvelope<RedeemParentInviteResponse>>("/parent/links/redeem", vars);
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.parent.children() });
    },
  });
}

/** DELETE /parent/links/{student_user_id} -> revoke a link from the parent side. */
export function useRevokeChildLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (student_user_id: string) => {
      const res = await api.delete<ApiEnvelope<{ ok: boolean }>>(
        `/parent/links/${encodeURIComponent(student_user_id)}`,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.parent.children() });
    },
  });
}

// ---------------------------------------------------------------------------
// Phase 1: child analytics dashboard
// ---------------------------------------------------------------------------

/** GET /parent/children/{student_user_id}/dashboard */
export function useChildDashboard(studentId: string | undefined) {
  return useQuery({
    queryKey: qk.parent.dashboard(studentId ?? ""),
    enabled: !!studentId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ChildDashboardResponse>>(
        `/parent/children/${encodeURIComponent(studentId!)}/dashboard`,
      );
      return unwrap(res.data);
    },
    staleTime: 30_000,
  });
}

/** GET /parent/children/{student_user_id}/heatmap?days= */
export function useChildHeatmap(studentId: string | undefined, days = 91) {
  return useQuery({
    queryKey: qk.parent.heatmap(studentId ?? "", days),
    enabled: !!studentId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<HeatmapDay[]>>(
        `/parent/children/${encodeURIComponent(studentId!)}/heatmap`,
        { params: { days } },
      );
      return unwrap(res.data) ?? [];
    },
    staleTime: 60_000,
  });
}

/** GET /parent/children/{student_user_id}/subjects/{subject_id} */
export function useChildSubject(studentId: string | undefined, subjectId: string | undefined) {
  return useQuery({
    queryKey: qk.parent.subject(studentId ?? "", subjectId ?? ""),
    enabled: !!studentId && !!subjectId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ChildSubjectChapterRow[]>>(
        `/parent/children/${encodeURIComponent(studentId!)}/subjects/${encodeURIComponent(subjectId!)}`,
      );
      return unwrap(res.data) ?? [];
    },
    staleTime: 30_000,
  });
}

/** GET /parent/children/{student_user_id}/weak-topics */
export function useChildWeakTopics(studentId: string | undefined) {
  return useQuery({
    queryKey: qk.parent.weakTopics(studentId ?? ""),
    enabled: !!studentId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<WeakTopic[]>>(
        `/parent/children/${encodeURIComponent(studentId!)}/weak-topics`,
      );
      return unwrap(res.data) ?? [];
    },
    staleTime: 60_000,
  });
}

/** GET /parent/children/{student_user_id}/activity?days= */
export function useChildActivity(studentId: string | undefined, days = 14) {
  return useQuery({
    queryKey: qk.parent.activity(studentId ?? "", days),
    enabled: !!studentId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ActivityItem[]>>(
        `/parent/children/${encodeURIComponent(studentId!)}/activity`,
        { params: { days } },
      );
      return unwrap(res.data) ?? [];
    },
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Phase 2: readiness score + pace tracker
// ---------------------------------------------------------------------------

/** GET /parent/children/{student_user_id}/readiness */
export function useChildReadiness(studentId: string | undefined) {
  return useQuery({
    queryKey: qk.parent.readiness(studentId ?? ""),
    enabled: !!studentId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ChildReadinessResponse>>(
        `/parent/children/${encodeURIComponent(studentId!)}/readiness`,
      );
      return unwrap(res.data);
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Phase 4: weekly digest + alerts
// ---------------------------------------------------------------------------

/** GET /parent/alerts?limit= */
export function useParentAlerts(limit = 30) {
  return useQuery({
    queryKey: qk.parent.alerts(limit),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ParentAlert[]>>("/parent/alerts", { params: { limit } });
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
  });
}

/** POST /parent/alerts/read {up_to} -> mark-read watermark. */
export function useMarkAlertsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (up_to: string) => {
      const res = await api.post<ApiEnvelope<{ marked: number }>>("/parent/alerts/read", { up_to });
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["parent", "alerts"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Phase 6: doubt patterns
// ---------------------------------------------------------------------------

/** GET /parent/children/{student_user_id}/doubt-patterns */
export function useChildDoubtPatterns(studentId: string | undefined) {
  return useQuery({
    queryKey: qk.parent.doubtPatterns(studentId ?? ""),
    enabled: !!studentId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<DoubtPattern[]>>(
        `/parent/children/${encodeURIComponent(studentId!)}/doubt-patterns`,
      );
      return unwrap(res.data) ?? [];
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Phase 7: Board Exam Mode
// ---------------------------------------------------------------------------

/** GET /parent/children/{student_user_id}/board-exam -- 404s for classes other than 10/12. */
export function useChildBoardExam(studentId: string | undefined) {
  return useQuery({
    queryKey: qk.parent.boardExam(studentId ?? ""),
    enabled: !!studentId,
    retry: false,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<BoardExamResponse>>(
        `/parent/children/${encodeURIComponent(studentId!)}/board-exam`,
      );
      return unwrap(res.data);
    },
    staleTime: 60_000,
  });
}

/** POST /parent/children/{student_user_id}/cram-plan */
export function useCreateCramPlan() {
  return useMutation({
    mutationFn: async (vars: { studentId: string } & CramPlanRequest) => {
      const { studentId, ...body } = vars;
      const res = await api.post<ApiEnvelope<CramPlanResponse>>(
        `/parent/children/${encodeURIComponent(studentId)}/cram-plan`,
        body,
      );
      return unwrap(res.data);
    },
  });
}

/** PUT /parent/children/{student_user_id}/notification-prefs */
export function useSetNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { studentId: string } & NotificationPrefs) => {
      const { studentId, ...body } = vars;
      const res = await api.put<ApiEnvelope<unknown>>(
        `/parent/children/${encodeURIComponent(studentId)}/notification-prefs`,
        body,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.parent.children() });
    },
  });
}
